import type { protocol } from "@codesandbox/pitcher-client";
import { PICKER_BROWSER_PACKAGE_MANAGER } from "@codesandbox/pitcher-client/dist/esm/browser";
import algoliasearch from "algoliasearch";

import type { Dependency } from "./types";

export type PackageManager =
  | "yarn"
  | "npm"
  | "pnpm"
  | typeof PICKER_BROWSER_PACKAGE_MANAGER;

export async function detectPackageManager(
  fileExist: (path: string) => Promise<boolean>,
): Promise<PackageManager> {
  const managerLockFiles: Record<string, PackageManager> = {
    "yarn.lock": "yarn",
    "package-lock.json": "npm",
    "pnpm-lock.yaml": "pnpm",
    "package.json": "yarn",
  };

  for (const managerPath of Object.keys(managerLockFiles)) {
    const exist = await fileExist(managerPath);

    if (exist) {
      return managerLockFiles[managerPath];
    }
  }

  return "yarn";
}

export function getInstallationScript({
  manager,
  dependency,
  dev,
  version,
}: {
  manager: PackageManager;
  dependency: string;
  dev: boolean;
  version: string;
}) {
  switch (manager) {
    case "pnpm":
    case "npm":
      return `${manager} install ${dependency}@${version}${
        dev ? " --save-dev" : ""
      }`;
    case PICKER_BROWSER_PACKAGE_MANAGER:
      return `${PICKER_BROWSER_PACKAGE_MANAGER} add ${dependency}@${version}${
        dev ? " --dev" : ""
      }`;
    case "yarn":
      return `yarn add ${dependency}@${version}${dev ? " --dev" : ""}`;
  }
}

export function getUninstallScript(
  manager: PackageManager,
  dependency: string,
) {
  switch (manager) {
    case "pnpm":
    case "npm":
      return `${manager} uninstall ${dependency}`;
    case PICKER_BROWSER_PACKAGE_MANAGER:
      return `${PICKER_BROWSER_PACKAGE_MANAGER} remove ${dependency}`;
    case "yarn":
      return `yarn remove ${dependency}`;
  }
}

export const findTaskStatus = (
  tasks: Record<string, protocol.task.TaskDTO>,
  projectDependencyManager: PackageManager,
  { name, dev, version }: Dependency,
): {
  status: protocol.shell.ShellProcessStatus;
  id?: string;
} => {
  const installCommand = Object.entries(tasks).find(([key]) => {
    return (
      key ===
        getInstallationScript({
          manager: projectDependencyManager,
          dependency: name,
          dev,
          version,
        }) ||
      key ===
        getInstallationScript({
          manager: projectDependencyManager,
          dependency: name,
          dev,
          version: "latest",
        })
    );
  })?.[1];

  const installStatus = installCommand?.shell?.status;
  if (installStatus === "ERROR" || installStatus === "RUNNING") {
    return {
      status: installStatus,
      id: installCommand?.id,
    };
  }

  const uninstallCommand = Object.entries(tasks).find(([key]) => {
    return key === getUninstallScript(projectDependencyManager, name);
  })?.[1];
  const uninstallStatus = uninstallCommand?.shell?.status;

  return {
    status: uninstallStatus || "FINISHED",
    id: uninstallCommand?.id,
  };
};

const client = algoliasearch("OFCNCOG2CU", "00383ecd8441ead30b1b0ff981c426f5");
const indexSearch = client.initIndex("npm-search");

export const getDependencyLatestVersion = async (dependencyName: string) => {
  const results = await indexSearch.getObject<{
    tags: { latest: string };
  }>(dependencyName, ["tags"]);

  return results.tags.latest;
};

export const onSearchDependency = async (
  query: string,
): Promise<Array<{ name: string; version: string }>> => {
  let queryWithVersion = query;
  let version: undefined | string = undefined;

  // This includes package scope, eg @babel/runtime
  if (queryWithVersion.startsWith("@")) {
    queryWithVersion = queryWithVersion.slice(1);
  }

  // This contains specific version, eg babel/runtime@latest
  if (queryWithVersion.indexOf("@") !== -1) {
    version = queryWithVersion.match(/@.+/)?.[0].replace("@", "");
    queryWithVersion = queryWithVersion.replace(/@.+/, "");
  }

  const data = await indexSearch.search<{
    name: string;
    version: string;
    tags: { latest: string };
    versions: Record<string, string>;
  }>(queryWithVersion);

  return data.hits.map((item) => {
    return {
      name: item.name,
      version: version && item.versions[version] ? version : item.tags.latest,
    };
  });
};
